<template>
  <v-container fill-height justify-content-start class="app-section-width">
    <v-layout column>
      <v-layout align-center class="mb-4">
        <v-btn icon light @click="$router.go(-1)" :large="mdAndUp">
          <v-icon color="primary">mdi-arrow-left</v-icon>
        </v-btn>
        <span class="display-1 primary--text text--darken-3 ml-2">
          {{ selectedProduct.name }}
        </span>
      </v-layout>
      <v-flex class="app-scroll-section pa-2">
        <v-form ref="form">
          <v-layout class="mt-4 mb-2">
            <div v-if="selectedProduct.coverImageUrl" class="cover-image mr-4">
              <placeholder-image :url="selectedProduct.coverImageUrl" />
            </div>
            <div class="subtitle-1 grey--text text--darken-3 pre-wrap">{{ selectedProduct.description }}</div>
          </v-layout>
          <v-layout v-if="amountAndCurrency && amountAndCurrency.amount" wrap>
            <v-chip label class="mt-2 mr-1 px-2" color="primary lighten-2" :text-color="basePriceChipTextColor">
              {{ getPriceString({ amountAndCurrency: amountAndCurrency }) }}
            </v-chip>
            <v-chip v-if="showDisplayCurrency" label class="mt-2 ml-1 px-2">
              {{ getPriceString({ amountAndCurrency: amountAndCurrency, displayCurrency: displayCurrency }) }}
            </v-chip>
          </v-layout>
          <modifier-selection :validate="() => { $refs.form.validate() }" />
          <p class="headline grey--text text--darken-1 mt-5">{{ $t("catalog.how_many") }}</p>
          <quantity-selector :productId="selectedProduct.id" class="px-4" />
          <div v-if="relatedProducts.length > 0">
            <p class="headline grey--text text--darken-1 mt-5">{{ $t("catalog.you_may_also_like") }}</p>
            <div v-for="(relatedProduct) in relatedProducts" :key="relatedProduct.id"
              :class="!state.loading && !relatedProduct.outOfStock ? 'clickable' : 'opacity'"
            >
              <related-product v-if="selectedProduct.active" :product="relatedProduct" />
            </div>
          </div>
        </v-form>
      </v-flex>
      <product-selection-button :class="{ 'ma-2': smAndUp }" :validate="() => $refs.form.validate()" />
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import reservationMixins from '@/mixins/Reservation'
import vuetifyMixins from '@/mixins/Vuetify'
import router from '@/router'
import store from '@/store'

import { textColor } from '@/lib/helpers/ColorUtils'

export default {
  beforeRouteEnter (to, from, next) {
    if (store.getters['activityReservations/isUnsaved']) {
      next()
    } else {
      router.go(-1)
    }
  },
  data () {
    return {
      basePriceChipTextColor: textColor(this.$vuetify.theme.themes.light.primary.lighten2),
    }
  },
  created () {
    this.$store.commit('cart/addProductToState', { product: this.selectedProduct })
    if (!this.selectedProduct.displayedModifiers.length) {
      const quantity = this.getBasicProductCartQuantity(this.selectedProduct.id)
      this.$store.commit('cart/setQuantity', { quantity: quantity || 1, productId: this.selectedProduct.id })
    } else {
      this.$store.commit('cart/setQuantity', { quantity: 1, productId: this.selectedProduct.id })
    }
  },
  computed: {
    quantity () {
      return this.stateProduct ? this.stateProduct.quantity : 0
    },
    productId () {
      return this.selectedProduct.id
    },
    relatedProducts () {
      const noCategoryProducts = this.catalog ? this.catalog.products : []
      const allCategoryProducts = []
      if (this.catalog) {
        this.catalog.categories.forEach(({ products }) => {
          allCategoryProducts.push(...products)
        })
      }
      const allProducts = [...noCategoryProducts, ...allCategoryProducts]
      return allProducts
        .map(productAndTax => productAndTax.product)
        .filter((product) => this.selectedProduct.relatedProductIds.includes(product.id) &&
          !product.outOfStock &&
          product.active)
    },
    amountAndCurrency () {
      const pGP = this.selectedProduct ? this.findPricingGroup({ pricingGroups: this.selectedProduct.pricingGroupsWithPrices }) : null
      return pGP ? pGP.prices[0][0] : null
    },
    ...mapGetters('me', ['getPriceString', 'showDisplayCurrency', 'displayCurrency']),
    ...mapGetters({
      stateProduct: 'cart/mainStateProduct',
      getBasicProductCartQuantity: 'cart/getBasicProductCartQuantity',
    }),
    ...mapState({
      cart: (state) => state.cart,
      state: (state) => state.state,
      products: ({ cart }) => cart.products,
      catalog: (state) => state.activities.catalog,
      selectedProduct: ({ cart }) => cart.selectedProduct,
      account: (state) => state.activityReservations.edited.account,
    }),
  },
  beforeDestroy () {
    this.$store.commit('cart/resetProducts')
    this.$store.commit('cart/resetTempCartProducts')
  },
  components: {
    ProductSelectionButton: () => import('@/components/view-components/catalog/ProductSelectionButton.vue'),
    ModifierSelection: () => import('@/components/view-components/catalog/ModifierSelection.vue'),
    QuantitySelector: () => import('@/components/view-components/catalog/QuantitySelector.vue'),
    PlaceholderImage: () => import('@/components/app-components/PlaceholderImage.vue'),
    RelatedProduct: () => import('@/components/view-components/catalog/RelatedProduct'),
  },
  mixins: [
    reservationMixins,
    vuetifyMixins,
  ],
}
</script>

<style lang="scss" scoped>
.product-name {
  font-weight: bold;
  color: grey;
  font-size: 18px;
}
.product-description {
  color: grey;
  font-size: 16px;
}
.cover-image {
  min-width: 80px;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  justify-content: center;
}
.opacity {
  opacity: 0.5;
}
</style>
